/**
 * ApiClient: A lightweight HTTP client for making API requests.
 *
 * NOTE: This client is a substitute and should only be used if absolutely necessary.
 * Prefer using more robust solutions like Axios or Fetch directly for better maintainability.
 */

import { exception } from 'services/ApplicationInsightsLog';
import { store } from 'store/store';

export interface ApiClientConfig {
	baseUrl?: string;
	defaultHeaders?: Record<string, string>;
	withCredentials?: boolean;
}

export interface ApiRequestOptions extends RequestInit {
	withAuthDetection?: boolean;
	skipJsonParse?: boolean;
}

export interface ApiResponse<T> {
	data?: T;
	error?: {
		message: string;
		status?: number;
		isAuthRedirect?: boolean;
		isNetworkError?: boolean;
	};
}

export class ApiClient {
	private readonly baseUrl: string;
	private readonly defaultHeaders: Record<string, string>;
	private readonly withCredentials: boolean;

	constructor(config: ApiClientConfig = {}) {
		this.baseUrl = config.baseUrl ?? '';
		this.defaultHeaders = config.defaultHeaders || {};
		this.withCredentials = config.withCredentials ?? true;
	}

	/**
	 * Builds request headers, including the theme from the store.
	 * @param customHeaders Optional custom headers to merge with defaults.
	 * @returns HeadersInit object.
	 */
	private buildHeaders(customHeaders?: HeadersInit): HeadersInit {
		const theme = store.getState().claimContent?.theme?.toUpperCase() ?? 'CODAN';
		return {
			'X-Site-Name': theme,
			...this.defaultHeaders,
			...(customHeaders || {}),
		};
	}

	/**
	 * Constructs a full URL with a timestamp to prevent caching.
	 * @param path The API endpoint path.
	 * @returns The full URL string.
	 */
	private buildUrl(path: string): string {
		const timestamp = Date.now();
		const base = `${this.baseUrl}${path}`;
		return base.includes('?') ? `${base}&_=${timestamp}` : `${base}?_=${timestamp}`;
	}

	/**
	 * Handles the API response, including JSON parsing and error handling.
	 * @param response The fetch response object.
	 * @param options The request options.
	 * @returns A promise resolving to an ApiResponse object.
	 */
	private async handleResponse<T>(response: Response, options: ApiRequestOptions): Promise<ApiResponse<T>> {
		if (options.withAuthDetection && (response.status === 302 || response.type === 'opaqueredirect')) {
			this.logAuthRedirect(response, options.method);
			return { error: { message: 'Auth redirect detected', status: 401, isAuthRedirect: true } };
		}

		if (!response.ok) {
			return { error: { message: `HTTP error: ${response.status}`, status: response.status } };
		}

		if (options.skipJsonParse) {
			return { data: response as any };
		}

		try {
			const text = await response.text();
			if (!text) {
				return { data: undefined as any };
			}
			const json = JSON.parse(text);
			return { data: json };
		} catch (e) {
			return { error: { message: 'Failed to parse JSON response' } };
		}
	}

	/**
	 * Makes an HTTP request using the Fetch API.
	 * @param path The API endpoint path.
	 * @param options The request options.
	 * @returns A promise resolving to an ApiResponse object.
	 */
	private async request<T>(path: string, options: ApiRequestOptions = {}): Promise<ApiResponse<T>> {
		const url = this.buildUrl(path);

		try {
			const response = await fetch(url, {
				...options,
				method: options.method ?? 'GET',
				headers: this.buildHeaders(options.headers),
				credentials: this.withCredentials ? 'include' : 'same-origin',
				redirect: 'manual',
			});

			return this.handleResponse<T>(response, options);
		} catch (error) {
			return { error: { message: 'Network error', isNetworkError: true } };
		}
	}

	/**
	 * Logs details about an authentication redirect.
	 * @param response The fetch response object.
	 * @param method The HTTP method used for the request.
	 */
	private readonly logAuthRedirect = (response: Response, method: string | undefined) => {
		const redirectInfo = {
			url: response.url,
			method: method ?? 'GET',
			statusAfterRedirect: response.status,
		};
		exception(new Error('Auth redirect detected, treating it as a 401 unauthorized'), undefined, redirectInfo);
	};

	/**
	 * Makes a GET request to the specified API endpoint.
	 * @param path The API endpoint path.
	 * @param options Optional request options.
	 * @returns A promise resolving to an ApiResponse object.
	 */
	get<T>(path: string, options?: ApiRequestOptions) {
		return this.request<T>(path, { ...options, method: 'GET' });
	}
}

/**
 * Default instance of the ApiClient.
 */
export const apiClient = new ApiClient({ baseUrl: '' });
